import React, { useState, useEffect } from 'react';
import { policiesAPI } from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';
import {
  FileText,
  Plus,
  Loader2,
  Search,
  X,
  Trash2,
  Edit3,
  CheckCircle,
  Clock,
  Eye,
  Download,
  History,
  Users
} from 'lucide-react';

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'access_control', label: 'Access Control' },
  { value: 'incident_response', label: 'Incident Response' },
  { value: 'data_protection', label: 'Data Protection' },
  { value: 'acceptable_use', label: 'Acceptable Use' },
  { value: 'network_security', label: 'Network Security' },
  { value: 'physical_security', label: 'Physical Security' },
  { value: 'vendor_management', label: 'Vendor Management' }
];

const STATUS_CONFIG = {
  draft: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'Draft' },
  review: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'In Review' },
  approved: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Approved' },
  published: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Published' },
  archived: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Archived' }
};

const PolicyLibrary = () => {
  const { notify } = useNotification();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [filter, setFilter] = useState({ category: 'all', status: 'all', search: '' });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', category: 'general', version: '1.0',
    status: 'draft', content: '', owner: '', approver: '',
    effectiveDate: '', reviewDate: '', relatedFrameworks: ''
  });

  useEffect(() => { fetchPolicies(); }, []);

  const fetchPolicies = async () => {
    try {
      const res = await policiesAPI.getAll();
      setPolicies(res.data || []);
    } catch (err) { notify.error('Failed to load policies'); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...form, relatedFrameworks: form.relatedFrameworks.split(',').map(s => s.trim()).filter(Boolean) };
      if (selectedPolicy) {
        await policiesAPI.update(selectedPolicy.id, data);
        notify.success('Policy updated');
      } else {
        await policiesAPI.create(data);
        notify.success('Policy created');
      }
      setShowModal(false); setSelectedPolicy(null);
      setForm({ title: '', description: '', category: 'general', version: '1.0', status: 'draft', content: '', owner: '', approver: '', effectiveDate: '', reviewDate: '', relatedFrameworks: '' });
      fetchPolicies();
    } catch (err) { notify.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (policyId) => {
    if (!window.confirm('Delete this policy?')) return;
    try {
      await policiesAPI.delete(policyId);
      notify.success('Policy deleted');
      fetchPolicies();
    } catch (err) { notify.error('Failed to delete'); }
  };

  const handleAcknowledge = async (policyId) => {
    try {
      await policiesAPI.acknowledge(policyId);
      notify.success('Policy acknowledged');
      fetchPolicies();
    } catch (err) { notify.error('Failed to acknowledge'); }
  };

  const filteredPolicies = policies.filter(p => {
    if (filter.category !== 'all' && p.category !== filter.category) return false;
    if (filter.status !== 'all' && p.status !== filter.status) return false;
    if (filter.search && !p.title.toLowerCase().includes(filter.search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><Loader2 className="w-10 h-10 text-violet-500 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-16">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2"><FileText className="w-7 h-7 text-blue-400" />Policy Library</h1>
            <p className="text-slate-400 mt-1">Manage security policies and procedures</p>
          </div>
          <button onClick={() => { setSelectedPolicy(null); setForm({ title: '', description: '', category: 'general', version: '1.0', status: 'draft', content: '', owner: '', approver: '', effectiveDate: '', reviewDate: '', relatedFrameworks: '' }); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"><Plus className="w-4 h-4" />New Policy</button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-4 mb-8">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"><div className="text-2xl font-bold text-white">{policies.length}</div><div className="text-sm text-slate-400">Total Policies</div></div>
          <div className="bg-slate-500/10 rounded-xl p-4 border border-slate-500/30"><div className="text-2xl font-bold text-slate-400">{policies.filter(p => p.status === 'draft').length}</div><div className="text-sm text-slate-400">Draft</div></div>
          <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30"><div className="text-2xl font-bold text-yellow-400">{policies.filter(p => p.status === 'review').length}</div><div className="text-sm text-yellow-300">In Review</div></div>
          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30"><div className="text-2xl font-bold text-green-400">{policies.filter(p => p.status === 'published').length}</div><div className="text-sm text-green-300">Published</div></div>
          <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30"><div className="text-2xl font-bold text-red-400">{policies.filter(p => new Date(p.reviewDate) < new Date() && p.status !== 'archived').length}</div><div className="text-sm text-red-300">Needs Review</div></div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Search policies..." value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })} className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white" />
          </div>
          <select value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })} className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white">
            <option value="all">All Categories</option>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white">
            <option value="all">All Status</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        {/* Policies List */}
        {filteredPolicies.length === 0 ? (
          <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center">
            <FileText className="w-16 h-16 text-slate-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Policies Found</h3>
            <p className="text-slate-400">Create your first security policy</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPolicies.map(policy => {
              const statusConfig = STATUS_CONFIG[policy.status] || STATUS_CONFIG.draft;
              const catConfig = CATEGORIES.find(c => c.value === policy.category) || CATEGORIES[0];
              const needsReview = new Date(policy.reviewDate) < new Date() && policy.status !== 'archived';

              return (
                <div key={policy.id} className="bg-slate-800/50 rounded-xl border border-slate-700 hover:border-slate-600 transition-colors overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-white truncate">{policy.title}</h3>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${statusConfig.bg} ${statusConfig.text}`}>{statusConfig.label}</span>
                          <span className="text-xs text-slate-500">v{policy.version}</span>
                          {needsReview && <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-400">Review Due</span>}
                        </div>
                      </div>
                    </div>
                    <p className="text-slate-400 text-sm mb-4 line-clamp-2">{policy.description || 'No description'}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                      <span>{catConfig.label}</span>
                      {policy.owner && <span>Owner: {policy.owner}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setSelectedPolicy(policy); setShowViewModal(true); }} className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm flex items-center justify-center gap-1"><Eye className="w-4 h-4" />View</button>
                      <button onClick={() => { setSelectedPolicy(policy); setForm({ ...policy, relatedFrameworks: policy.relatedFrameworks?.join(', ') || '' }); setShowModal(true); }} className="p-2 text-slate-400 hover:text-violet-400 hover:bg-slate-700 rounded-lg"><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(policy.id)} className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {policy.acknowledgments?.length > 0 && (
                    <div className="px-6 py-3 border-t border-slate-700 bg-slate-800/30">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Users className="w-3 h-3" />
                        {policy.acknowledgments.length} acknowledgment(s)
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* View Modal */}
        {showViewModal && selectedPolicy && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-3xl border border-slate-700 shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800">
                <div>
                  <h3 className="text-xl font-semibold text-white">{selectedPolicy.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_CONFIG[selectedPolicy.status]?.bg} ${STATUS_CONFIG[selectedPolicy.status]?.text}`}>{STATUS_CONFIG[selectedPolicy.status]?.label}</span>
                    <span className="text-xs text-slate-500">Version {selectedPolicy.version}</span>
                  </div>
                </div>
                <button onClick={() => setShowViewModal(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6">
                <div className="prose prose-invert max-w-none">
                  <div className="whitespace-pre-wrap text-slate-300">{selectedPolicy.content || 'No content available.'}</div>
                </div>
                {selectedPolicy.versionHistory?.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-slate-700">
                    <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2"><History className="w-4 h-4" />Version History</h4>
                    <div className="space-y-2">
                      {selectedPolicy.versionHistory.map((v, idx) => (
                        <div key={idx} className="p-3 bg-slate-700/30 rounded-lg text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-white">v{v.version}</span>
                            <span className="text-slate-500">{new Date(v.changedAt).toLocaleDateString()}</span>
                          </div>
                          <p className="text-slate-400 mt-1">{v.changes}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="p-6 border-t border-slate-700 flex gap-3">
                <button onClick={() => handleAcknowledge(selectedPolicy.id)} className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2"><CheckCircle className="w-4 h-4" />Acknowledge</button>
                <button onClick={() => setShowViewModal(false)} className="px-4 py-2 border border-slate-600 text-slate-300 rounded-lg">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-2xl border border-slate-700 shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-700 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">{selectedPolicy ? 'Edit Policy' : 'New Policy'}</h3>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Title *</label>
                  <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" required />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Category</label>
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Version</label>
                    <input type="text" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Status</label>
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Description</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white resize-none" />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Policy Content</label>
                  <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={8} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white resize-none font-mono text-sm" placeholder="Enter the full policy text..." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Owner</label>
                    <input type="text" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Approver</label>
                    <input type="text" value={form.approver} onChange={(e) => setForm({ ...form, approver: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Effective Date</label>
                    <input type="date" value={form.effectiveDate} onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Review Date</label>
                    <input type="date" value={form.reviewDate} onChange={(e) => setForm({ ...form, reviewDate: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                  </div>
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Related Frameworks (comma-separated)</label>
                  <input type="text" placeholder="e.g., HIPAA, NIST, SOC2" value={form.relatedFrameworks} onChange={(e) => setForm({ ...form, relatedFrameworks: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg">Cancel</button>
                  <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">{saving ? 'Saving...' : 'Save Policy'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PolicyLibrary;
